import React, { useState, useEffect, useCallback, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, Flame, Brain, Dumbbell, Trophy, Scale } from "lucide-react";
import { toast } from "sonner";
import { shareAchievement, shareStreak } from "../utils/share";
import { AchievementCard, StreakBanner } from "../components/AchievementWidgets";
import { devError } from "../utils/devLogger";

const Achievements = () => {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [streak, setStreak] = useState(null);
  const [totalUnlocked, setTotalUnlocked] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const fetchAchievements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/achievements`);
      setAchievements(response.data.achievements);
      setStreak(response.data.streak);
      setTotalUnlocked(response.data.total_unlocked);
    } catch (error) {
      devError("Failed to fetch achievements:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAchievements();
  }, [fetchAchievements]);


  const categories = [
    { id: "all", labelKey: "achievements.categories.all", icon: Trophy },
    { id: "streak", labelKey: "achievements.categories.streak", icon: Flame },
    { id: "physical", labelKey: "achievements.categories.physical", icon: Dumbbell },
    { id: "cognitive", labelKey: "achievements.categories.cognitive", icon: Brain },
    { id: "balance", labelKey: "achievements.categories.balance", icon: Scale }
  ];

  const filteredAchievements = selectedCategory === "all" 
    ? achievements 
    : achievements.filter(a => a.category === selectedCategory);

  const unlockedInCategory = filteredAchievements.filter(a => a.unlocked).length;

  const handleUseFreeze = async () => {
    try {
      const res = await axios.post(`${API}/streak/freeze`);
      toast.success(res.data.message);
      fetchAchievements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to use freeze");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sky-50 flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-sky-400/30 glow-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sky-50" data-testid="achievements-page">
      <div className="fixed top-0 right-0 w-96 h-96 bg-amber-300/10 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-72 h-72 bg-indigo-300/15 rounded-full blur-3xl pointer-events-none" />

      <header className="fixed top-0 w-full z-50 glass-header">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="text-sky-600 hover:text-sky-800"
              data-testid="back-to-dashboard-btn"
            >
              <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
              {t('common.dashboard')}
            </Button>
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" strokeWidth={1.5} />
              <span className="text-sm text-sky-700">{t('achievements.title')}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-5xl mx-auto">
          <StreakBanner streak={streak} shareStreak={shareStreak} onUseFreeze={handleUseFreeze} t={t} />

          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map(cat => {
              const Icon = cat.icon;
              const isActive = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-full transition-all
                    ${isActive 
                      ? 'bg-sky-400 text-white shadow-lg' 
                      : 'bg-white/60 text-sky-700 hover:bg-white/80'
                    }
                  `}
                  data-testid={`filter-${cat.id}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span className="text-sm font-medium">{t(cat.labelKey)}</span>
                </button>
              );
            })}
          </div>

          <div className="glass-card p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-sky-700">
                {unlockedInCategory} / {filteredAchievements.length} {t('achievements.unlocked')}
              </span>
              <span className="text-sm font-medium text-sky-500">
                {Math.round((unlockedInCategory / filteredAchievements.length) * 100) || 0}%
              </span>
            </div>
            <Progress 
              value={(unlockedInCategory / filteredAchievements.length) * 100 || 0} 
              className="h-2 mt-2 bg-sky-100" 
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAchievements.map((achievement) => (
              <AchievementCard
                key={achievement.id}
                achievement={achievement}
                shareAchievement={shareAchievement}
                t={t}
              />
            ))}
          </div>

          {filteredAchievements.length === 0 && (
            <div className="glass-card p-12 text-center">
              <Trophy className="w-16 h-16 mx-auto mb-4 text-sky-300" strokeWidth={1} />
              <p className="text-sky-700">{t('achievements.title')}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Achievements;
